import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import {
  getAccountTimezone,
  updateAccountTimezone,
} from "../apis/settings";
import {
  getActiveTimezone,
  getFallbackTimezone,
  setActiveTimezone,
} from "../utils/timestampUtils";
import { isAuthenticated } from "../utils/auth";

/**
 * Account timezone context.
 *
 * Holds the currently-active display timezone for the app. Source of truth:
 *   1. Server (`GET /api/settings/timezone`)
 *   2. localStorage cache (so we don't flash the wrong zone on reload)
 *   3. Browser-detected zone (`moment.tz.guess()`)
 *
 * Whenever the value changes we keep `timestampUtils.activeTimezone` in sync
 * so plain (non-React) callers like context formatters render the new zone.
 *
 * Only super_admin can edit; agents see the inherited account value as
 * read-only.
 */

const TimezoneContext = createContext({
  timezone: getFallbackTimezone(),
  fallbackTimezone: getFallbackTimezone(),
  canEdit: false,
  loading: false,
  saving: false,
  availableTimezones: [],
  setTimezone: async () => {},
  refresh: async () => {},
});

const CACHE_KEY = "accountTimezone";

function readCachedTimezone() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached && moment.tz.zone(cached)) return cached;
  } catch (_) {
    // ignore localStorage failures (private mode, etc.)
  }
  return null;
}

function writeCachedTimezone(tz) {
  try {
    if (tz) {
      localStorage.setItem(CACHE_KEY, tz);
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch (_) {
    // ignore
  }
}

function readUserRole() {
  try {
    const raw = localStorage.getItem("user");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.role) return parsed.role;
    }
  } catch (_) {
    // ignore
  }
  if (localStorage.getItem("userRole") === "agent") return "agent";
  return null;
}

export const TimezoneProvider = ({ children }) => {
  const fallback = getFallbackTimezone();
  // Initial value: cached > fallback. Active util is primed immediately so the
  // first render doesn't flash a wrong zone for already-mounted components.
  const initial = readCachedTimezone() || fallback;
  if (getActiveTimezone() !== initial) {
    setActiveTimezone(initial);
  }

  const [timezone, setTimezoneState] = useState(initial);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyTimezone = useCallback((tz) => {
    const next = tz && moment.tz.zone(tz) ? tz : fallback;
    setActiveTimezone(next);
    writeCachedTimezone(tz || null);
    setTimezoneState(next);
  }, [fallback]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated()) return;
    setLoading(true);
    try {
      const res = await getAccountTimezone();
      const tz = res?.data?.timezone || null;
      setCanEdit(!!res?.data?.can_edit);
      applyTimezone(tz);
    } catch (err) {
      // Stay on cached/fallback; assume read-only if we can't even fetch.
      const role = readUserRole();
      setCanEdit(role === "super_admin");
    } finally {
      setLoading(false);
    }
  }, [applyTimezone]);

  // Initial fetch on mount (only when authenticated).
  useEffect(() => {
    if (!isAuthenticated()) return;
    refresh();
  }, [refresh]);

  // Persist + push updates to the server (super_admin only).
  const setTimezone = useCallback(
    async (nextTz) => {
      const normalized = nextTz && moment.tz.zone(nextTz) ? nextTz : null;
      setSaving(true);
      try {
        const res = await updateAccountTimezone(normalized);
        const tz = res?.data?.timezone || null;
        applyTimezone(tz);
        toast.success("Timezone updated");
        return { success: true, timezone: tz };
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update timezone";
        toast.error(message);
        return { success: false, message };
      } finally {
        setSaving(false);
      }
    },
    [applyTimezone]
  );

  const availableTimezones = useMemo(() => {
    // Pre-computed list with a stable label including the current UTC offset.
    // Sorted alphabetically — picker can search by either name or offset.
    const now = Date.now();
    return moment.tz.names().map((name) => {
      const offset = moment.tz(now, name).format("Z");
      return { name, label: `(UTC${offset}) ${name}` };
    });
  }, []);

  const value = useMemo(
    () => ({
      timezone,
      fallbackTimezone: fallback,
      canEdit,
      loading,
      saving,
      availableTimezones,
      setTimezone,
      refresh,
    }),
    [
      timezone,
      fallback,
      canEdit,
      loading,
      saving,
      availableTimezones,
      setTimezone,
      refresh,
    ]
  );

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => useContext(TimezoneContext);

export default TimezoneContext;
